var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
const removeBracesRegExp = /(?:\[\s*\d+\])?({.+})/;
export class ZeeVee {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.deviceList = {};
        this.presetList = [];
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                const resp = (yield this.socket.send('show server info', (resp) => {
                    var _a, _b;
                    return ((_b = (_a = resp.command) === null || _a === void 0 ? void 0 : _a.startsWith) === null || _b === void 0 ? void 0 : _b.call(_a, 'show server info')) ? resp : undefined;
                }, tags));
                if (resp.status !== 'Success') {
                    this.log.error(`received error: ${JSON.stringify(resp)}`);
                    return 'unknown';
                }
                return (_c = (_b = (_a = resp.text) === null || _a === void 0 ? void 0 : _a.gen) === null || _b === void 0 ? void 0 : _b.version) !== null && _c !== void 0 ? _c : 'unknown';
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const resp = (yield this.socket.send(`show device status ${key}`, (resp) => {
                    var _a, _b;
                    return ((_b = (_a = resp.command) === null || _a === void 0 ? void 0 : _a.startsWith) === null || _b === void 0 ? void 0 : _b.call(_a, `show device status ${key}`))
                        ? resp
                        : undefined;
                }, tags));
                const deviceInfo = resp.text ? resp.text[0] : undefined;
                if (deviceInfo &&
                    deviceInfo.gen.mac !== undefined &&
                    ((_d = deviceInfo.connectedEncoder) === null || _d === void 0 ? void 0 : _d.mac) !== undefined &&
                    deviceInfo.gen.type === 'decoder') {
                    return deviceInfo.connectedEncoder.mac;
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _e, _f, _g;
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 1500);
                let resp = (yield this.socket.send(
                // join encoder/input decoder/output
                `join ${value} ${key} fastSwitched`, (resp) => {
                    var _a, _b;
                    return ((_b = (_a = resp.command) === null || _a === void 0 ? void 0 : _a.startsWith) === null || _b === void 0 ? void 0 : _b.call(_a, `join ${value} ${key}`)) ? resp : undefined;
                }, tags));
                if (((_e = resp.errors) === null || _e === void 0 ? void 0 : _e.length) !== 0) {
                    this.log.warning(`received video switching error: ${JSON.stringify(resp.errors)}`);
                }
                if (((_f = resp === null || resp === void 0 ? void 0 : resp.status) !== null && _f !== void 0 ? _f : '').toLowerCase() !== 'success') {
                    return this.state.videoMatrixRoutes.values[key];
                }
                // audio follows video
                resp = (yield this.socket.send(`join videoSource ${key} hdmiAudio`, (resp) => {
                    var _a, _b;
                    return ((_b = (_a = resp.command) === null || _a === void 0 ? void 0 : _a.startsWith) === null || _b === void 0 ? void 0 : _b.call(_a, `join videoSource`)) ? resp : undefined;
                }, [...tags, 'audioFollowsVideo']));
                if (((_g = resp.errors) === null || _g === void 0 ? void 0 : _g.length) !== 0) {
                    this.log.warning(`received audio switching error: ${JSON.stringify(resp.errors)}`);
                }
                return value;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                controls: ['videoMatrixRoutes'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            let result = 'verifyFailed';
            const initializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    unterminatedRegExp: /password:|Zyper\$/i,
                    next: /password:/i,
                    succeed: /Zyper\$/i,
                },
                {
                    errorCode: 'authFailed',
                    send: (cred) => {
                        var _a;
                        return (_a = cred.password) !== null && _a !== void 0 ? _a : '';
                    },
                    unterminatedRegExp: /Zyper\$|Incorrect\s*password/i,
                    next: /Zyper\$/i,
                    fail: /Incorrect\s+password/i,
                },
            ];
            result = yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
            if (result === 'connected') {
                // Set response mode to JSON mode for easier parsing of responses.
                const responseMode = (yield this.socket.send(`set terminal output json echo no prompt no`, (resp) => {
                    return resp.status === 'Success' ? resp : undefined;
                }, ['jsonFormat', 'initialize']));
                if (responseMode.status !== 'Success') {
                    this.log.error(`ZeeVee system not in JSON mode.`);
                }
                // Get the list of encoders / decoders for detected channels.
                yield this.queryDeviceList(['getDeviceList', 'initialize']);
                // Get the list of presets for future support.
                yield this.queryPresetList(['getPresetList', 'initialize']);
            }
            return result;
        });
    }
    // other class methods
    getDetected() {
        const videoInputs = [];
        const videoOutputs = [];
        for (const [key, device] of Object.entries(this.deviceList)) {
            if (device.type === 'encoder') {
                videoInputs.push({
                    code: key,
                    external: true,
                    name: device.name,
                });
            }
            else {
                videoOutputs.push({ code: key, name: device.name });
            }
        }
        // Add the 'none' source to support clearing the route to the output.
        videoInputs.push({ code: 'none', external: false, name: 'No source' });
        return { videoInputs, videoOutputs };
    }
    queryDeviceList(tags, updateDetected = false, updateMatrix = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.socket.send('show device status all', (resp) => {
                const code = resp.status === 'Success';
                return code ? resp : undefined;
            }, tags, { sendTimeoutMs: 6000 }));
            const text = (_a = resp.text) !== null && _a !== void 0 ? _a : [];
            if (!Array.isArray(text)) {
                this.log.error(`no routes in "show device status all" response`);
                return;
            }
            this.updateDeviceList(text);
            if (updateDetected)
                this.state.detected.update(this.getDetected());
            if (updateMatrix)
                this.updateVideoMatrixRoutes(text);
        });
    }
    queryPresetList(tags, updateDetected = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.presetList.length = 0;
            const resp = (yield this.socket.send('show preset all commandBlob', (resp) => {
                const code = resp.status === 'Success';
                return code ? resp : undefined;
            }, tags));
            const presets = (_a = resp.text) !== null && _a !== void 0 ? _a : [];
            if (!Array.isArray(presets)) {
                this.log.error(`invalid response to "show preset all commandBlob" response`);
                return;
            }
            presets.forEach(preset => {
                var _a;
                const name = (_a = preset.gen) === null || _a === void 0 ? void 0 : _a.name;
                if (typeof name === 'string')
                    this.presetList.push(name);
            });
        });
    }
    updateDeviceList(devices) {
        this.deviceList = {};
        devices.forEach(device => {
            var _a, _b, _c, _d;
            if (((_a = device.gen) === null || _a === void 0 ? void 0 : _a.mac) === undefined) {
                this.log.error(`missing mac for device ${JSON.stringify(device)}`);
                return;
            }
            if (((_b = device.gen) === null || _b === void 0 ? void 0 : _b.type) === undefined) {
                this.log.error(`missing type for device ${JSON.stringify(device)}`);
                return;
            }
            this.deviceList[device.gen.mac] = {
                type: device.gen.type,
                model: (_c = device.gen.model) !== null && _c !== void 0 ? _c : 'unknown',
                name: (_d = device.gen.name) !== null && _d !== void 0 ? _d : device.gen.mac,
            };
        });
    }
    updateVideoMatrixRoutes(devices) {
        devices.forEach(device => {
            var _a, _b, _c;
            if (((_a = device.gen) === null || _a === void 0 ? void 0 : _a.mac) !== undefined &&
                ((_b = device.connectedEncoder) === null || _b === void 0 ? void 0 : _b.mac) !== undefined &&
                ((_c = device.gen) === null || _c === void 0 ? void 0 : _c.type) === 'decoder') {
                this.state.videoMatrixRoutes.update(device.gen.mac, device.connectedEncoder.mac);
            }
        });
    }
}
// static properties
ZeeVee.driverType = 'socketDriver';
ZeeVee.key = '-NCabdQuIWeZXxONZEJc';
ZeeVee.socketOptions = [
    {
        // The ZeeVee API uses non-JSON commands during authentication and initialization, and
        // then carriage return terminated JSON once in JSON terminal mode.
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        parser: (command) => {
            try {
                // In some versions of ZeeVee firmware, JSON commands are prefixed with brackets
                // with a number inside, e.g., [ 1234].  Other responses are just text values.
                const match = removeBracesRegExp.exec(command);
                if (match) {
                    try {
                        const result = JSON.parse(match[1]);
                        return result;
                    }
                    catch (error) {
                        return command; // if not valid response, return the command
                    }
                }
                return command; // initial password and command prompt responses are not JSON
                //
            }
            catch (err) {
                return undefined;
            }
        },
        port: 23,
        sendTerminator: '\r\n',
    },
];
